import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useProductSettings } from '@workspace/utils-react';
import type { Settings } from '../../AppContainer';
import {
  getUserPermissions,
  searchUserPermissions,
  upsertUserPermission,
  deleteUserPermission,
} from '../../api/user-permissions';
import type { UpsertUserPermissionRequest } from '../../api/user-permissions';

export const userPermissionsQueryKey = ['user-permissions'] as const;

export function useUserPermissions() {
  const { apiUrl } = useProductSettings<Settings>();
  return useQuery({
    queryKey: userPermissionsQueryKey,
    queryFn: () => getUserPermissions(apiUrl),
    staleTime: 30 * 1000,
  });
}

export function useUserPermissionSearch(search: string) {
  const { apiUrl } = useProductSettings<Settings>();
  const term = search.trim();
  return useQuery({
    queryKey: [...userPermissionsQueryKey, 'search', term],
    queryFn: () => searchUserPermissions(apiUrl, term),
    enabled: term.length > 0,
    staleTime: 30 * 1000,
  });
}

export function useUpsertUserPermission() {
  const { apiUrl } = useProductSettings<Settings>();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UpsertUserPermissionRequest) => upsertUserPermission(apiUrl, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userPermissionsQueryKey });
    },
  });
}

export function useDeleteUserPermission() {
  const { apiUrl } = useProductSettings<Settings>();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (email: string) => deleteUserPermission(apiUrl, email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userPermissionsQueryKey });
    },
  });
}
